import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";

const IMG_BOARDROOM =
  "https://images.unsplash.com/photo-1431540015161-0bf868a2d407?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_TEAM =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85";

const PILLARS = [
  {
    n: "01",
    title: "Radical transparency",
    desc: "No softened status reports. You get the real picture and a clear plan to move forward.",
  },
  {
    n: "02",
    title: "Speed over bureaucracy",
    desc: "We integrate with your team in days, not months, and mobilise quickly when timing is tight.",
  },
  {
    n: "03",
    title: "Cross-domain fluency",
    desc: "We translate between engineers, operations and the board, so everyone is aligned and decisions happen faster.",
  },
];

const ENGAGEMENT = ["Short-term", "Long-term", "Quick turnaround", "Urgent & last-minute"];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="scroll-mt-24 border-t border-white/8 bg-abyss/40 py-20 sm:py-28 lg:py-32"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8">
        <div>
          <Reveal>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
              Why Astra / The operating model
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.75rem] lg:leading-[1.15]">
              PMs who act like air traffic controllers, not note-takers.
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <p
              data-testid="about-story-text"
              className="mt-7 leading-relaxed text-slate-400 sm:text-lg"
            >
              Projects rarely fail for technical reasons. They stall when
              decisions, ownership and governance break down. Astra Consult was
              founded on a simple conviction: senior project managers should own
              the plan, the room and the timeline. We step into live projects,
              bring clarity and control, and carry initiatives across the line,
              whether that means leading delivery end to end or turning around a
              programme that's lost momentum.
            </p>
          </Reveal>

          <div className="mt-10 space-y-0">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={0.1 + i * 0.08}>
                <div className="flex gap-6 border-t border-white/8 py-6">
                  <span className="font-mono text-sm text-gold">{p.n}</span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white">{p.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-6 flex flex-wrap gap-2">
              {ENGAGEMENT.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-plate/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-gold" />
                  {e}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="relative pb-16 pl-4 sm:pl-8 lg:pb-20 lg:pl-10">
          <Reveal delay={0.1}>
            <motion.div style={{ y: imgY }} className="relative">
              <div className="overflow-hidden rounded-2xl border border-white/10">
                <img
                  src={IMG_BOARDROOM}
                  alt="Executive governance and turnaround war room"
                  className="aspect-[4/3] w-full object-cover duotone-gold"
                  loading="lazy"
                />
              </div>
              <div className="absolute -top-3 right-4 rounded-full border border-white/10 bg-obsidian/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 backdrop-blur">
                Turnaround war room
              </div>
            </motion.div>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="absolute -bottom-2 left-0 w-1/2 max-w-[260px] overflow-hidden rounded-xl border border-white/15 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.9)] sm:left-4">
              <img
                src={IMG_TEAM}
                alt="Principal PM on-site intervention"
                className="aspect-[4/3] w-full object-cover duotone-gold"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
              <div className="scanlines absolute inset-0 opacity-50" />
              <p className="absolute bottom-2.5 left-3 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-300">
                Principal PM on-site
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
